import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Paper,
  Card,
  CardContent,
  CardHeader,
  Grid,
  useTheme,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Collapse,
  CircularProgress,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ListDataItem from './ListDataItem'
import { getLocation, getSpecialty } from 'pages/Jobs/helpers'
import PhysicianSignUpDialog from '../Dialog/PhysicianSignUpDialog'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'
import { fetchAIJobQuestion } from 'src/redux/aiJobSearch'
import { format } from 'date-fns'

export const ListItemJob = ({ data, isActive, onClick, onFavoriteClick, isAiMode = false }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const location = getLocation(data.city, data.state)
  const specialty = getSpecialty(data.specialties, data.subspecialties)
  const { employerUserId, signedIn } = useSelector(state => state.identity)
  const { jobQuestions, jobQuestionsLoading } = useSelector(state => state.aiJobSearch)
  const [openDialog, setOpenDialog] = React.useState(false)
  const [showQuestions, setShowQuestions] = React.useState(false)
  const [currentQuestion, setCurrentQuestion] = React.useState('')

  const onDialogClose = () => {
    setOpenDialog(false)
  }
  const handleDialogOpen = () => {
    setOpenDialog(true)
    dispatch(
      fetchTrackEvent({
        eventName: 'favorite_lead_click',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
        eventData: { jobId: data.id },
      }),
    )
  }

  const handleQuestionSubmit = () => {
    if (currentQuestion.trim()) {
      dispatch(fetchAIJobQuestion({ jobId: data.id, question: currentQuestion.trim() }))
      setCurrentQuestion('')
    }
  }

  const handleQuestionKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      handleQuestionSubmit()
    }
  }

  const jobQuestionResponses = jobQuestions[data.id] || []
  const isQuestionLoading = jobQuestionsLoading[data.id] || false

  const formattedDate = data.createdAt ? format(new Date(data.createdAt), 'MMMM dd, yyyy') : ''

  return (
    <Paper
      elevation={0}
      sx={{ 
        cursor: 'pointer', 
        position: 'relative',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)', // Much softer shadow
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderColor: theme.palette.primary.light,
        }
      }}
      onClick={() => onClick(data.id)}
    >
      <Card
        sx={{
          minWidth: 275,
          backgroundColor: theme.palette.common.white, // White background for all cards
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
          ...(isActive && {
            backgroundColor: 'rgba(118, 245, 172, 0.15)', // Very subtle light green tint for active state
            borderColor: theme.palette.primary.light,
          }),
        }}
      >
        <Grid container direction={'row'} spacing={1} wrap={'nowrap'}>
          <Grid item xs={10}>
            <CardHeader
              style={{ overflowWrap: 'break-word' }}
              title={data.title}
              titleTypographyProps={{ variant: 'h6' }}
              subheader={
                <>
                  {data.employerName}
                  {formattedDate && (
                    <Typography
                      variant='caption'
                      color='textSecondary'
                      sx={{ mt: 0.5, display: 'block', fontSize: '0.8rem' }}
                    >
                      Posted on: {formattedDate}
                    </Typography>
                  )}
                </>
              }
              sx={{
                display: 'flex',
                overflow: 'hidden',
                '& .MuiCardHeader-content': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                },
              }}
            />
          </Grid>
          <Grid
            item
            xs={2}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {!data.deletedAt && !employerUserId && (
              <div onClick={e => e.stopPropagation()}>
                <IconButton
                  aria-label='favorite'
                  onClick={e => {
                    e.stopPropagation()
                    signedIn
                      ? onFavoriteClick({
                          jobId: data.id,
                          favorite: data.candidateJob ? !data.candidateJob.favorite : true,
                        })
                      : handleDialogOpen()
                  }}
                >
                  {data.candidateJob && data.candidateJob.favorite ? (
                    <FavoriteIcon color='error' />
                  ) : (
                    <FavoriteBorderIcon color='error' />
                  )}
                </IconButton>
                <PhysicianSignUpDialog
                  openDialog={openDialog}
                  handleDialogClose={onDialogClose}
                  feature='favorite'
                />
              </div>
            )}
          </Grid>
        </Grid>
        <CardContent>
          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 12, sm: 12, md: 12 }}>
            <Grid item xs={12} sm={12} md={6}>
              {location && <ListDataItem icon={<LocationOnIcon />} text={location} />}
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              {specialty && <ListDataItem icon={<LocalHospitalIcon />} text={specialty} />}
            </Grid>
          </Grid>
        </CardContent>

        {/* AI Question Section */}
        {isAiMode && (
          <>
            <Box sx={{ p: 2 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 1, 
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowQuestions(!showQuestions)
                }}
              >
                <QuestionAnswerIcon sx={{ color: theme.palette.secondary.main, fontSize: '1.1rem' }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.secondary.main, 
                    fontWeight: 'medium',
                    fontFamily: 'Kumbh Sans, sans-serif'
                  }}
                >
                  Ask AI about this job
                </Typography>
                {showQuestions ? <ExpandLessIcon sx={{ color: theme.palette.secondary.main }} /> : <ExpandMoreIcon sx={{ color: theme.palette.secondary.main }} />}
              </Box>
              
              <Collapse in={showQuestions}>
                <Box sx={{ mt: 2 }}>
                  {/* Question Input */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }} onClick={(e) => e.stopPropagation()}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Ask a question about this job..."
                      value={currentQuestion}
                      onChange={(e) => setCurrentQuestion(e.target.value)}
                      onKeyPress={handleQuestionKeyPress}
                      disabled={isQuestionLoading}
                      multiline
                      maxRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontFamily: 'Kumbh Sans, sans-serif',
                          fontSize: '0.875rem',
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.secondary.main,
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleQuestionSubmit()
                      }}
                      disabled={!currentQuestion.trim() || isQuestionLoading}
                      sx={{
                        backgroundColor: theme.palette.secondary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.secondary.dark,
                        },
                        fontFamily: 'Kumbh Sans, sans-serif',
                        minWidth: 'auto',
                        px: 2,
                      }}
                    >
                      {isQuestionLoading ? <CircularProgress size={20} color="inherit" /> : 'Ask'}
                    </Button>
                  </Box>

                  {/* Question Responses */}
                  {jobQuestionResponses.length > 0 && (
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                      {jobQuestionResponses.map((qa, index) => (
                        <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < jobQuestionResponses.length - 1 ? `1px solid ${theme.palette.grey[300]}` : 'none' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'medium', 
                              color: theme.palette.grey[900],
                              fontFamily: 'Kumbh Sans, sans-serif',
                              mb: 1
                            }}
                          >
                            Q: {qa.question}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.grey[600],
                              fontFamily: 'Kumbh Sans, sans-serif',
                              lineHeight: 1.5
                            }}
                          >
                            A: {qa.answer}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          </>
        )}
      </Card>

      {data.deletedAt && (
        <Typography
          variant='overline'
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: theme.palette.info.light,
            color: theme.palette.common.white,
            padding: '2px 4px',
            borderRadius: '1px',
          }}
        >
          Archived
        </Typography>
      )}
    </Paper>
  )
}
